
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAdminStats, getLiveVisitorsCount } from '@/lib/api';
import { DateRangeFilter, type DateRangeType } from './DateRangeFilter';
import { DashboardCharts } from './DashboardCharts';
import { Card } from '@/components/ui/card';
import { Loader2, Users, AlertCircle, Info } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

interface Stats {
  totalUsers: number;
  totalCourses: number;
  totalRevenue: number;
  liveVisitors?: number;
  timeSeriesData?: {
    visitors?: any[];
    courses?: any[];
    revenue?: any[];
  };
}

interface AdminDashboardContentProps {
  adminUser: {
    email: string | undefined;
    id: string;
  };
}

export function AdminDashboardContent({
  adminUser
}: AdminDashboardContentProps) {
  // Set default to 'today'
  const [dateRange, setDateRange] = useState<DateRangeType>('today');
  const [liveVisitors, setLiveVisitors] = useState<number | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [liveVisitorsError, setLiveVisitorsError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  const {
    data: stats,
    isLoading,
    isError,
    error
  } = useQuery<Stats, Error>({
    queryKey: ['adminStats', dateRange],
    queryFn: () => getAdminStats(dateRange),
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });
  
  // Live visitors polling mechanism - only run when dateRange is 'today'
  useEffect(() => {
    if (dateRange !== 'today') {
      setIsPolling(false);
      return;
    }
    
    const fetchLiveVisitors = async () => {
      try {
        setIsPolling(true);
        setLiveVisitorsError(null);
        const count = await getLiveVisitorsCount();
        setLiveVisitors(count);
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Error fetching live visitors:', error);
        setLiveVisitorsError('Failed to fetch live visitor data');
        setLiveVisitors(0);
      }
    };

    // Initial fetch
    fetchLiveVisitors();

    // Set up polling every 45 seconds
    const interval = setInterval(fetchLiveVisitors, 45000);

    return () => {
      clearInterval(interval);
      setIsPolling(false);
    };
  }, [dateRange]);
  
  if (isLoading) {
    return <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin mr-2" />
      <p>Loading stats...</p>
    </div>;
  }
  
  if (isError) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4 flex items-start">
          <AlertCircle className="text-red-500 mr-3 h-5 w-5 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-800">Failed to load dashboard data</h3>
            <p className="text-red-700 text-sm mt-1">{error?.message || 'Please try again later'}</p>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
          <h1 className="text-xl md:text-2xl font-semibold mb-3 md:mb-0">
            Welcome back, {adminUser.email?.split('@')[0] || 'Admin'}
          </h1>
          <div className={isMobile ? "mt-2" : ""}>
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
          </div>
        </div>
        
        <DashboardCharts 
          visitors={0} 
          signups={0} 
          revenue={0} 
          dateRange={dateRange}
          isLoading={false}
          timeSeriesData={{}}
        />
      </div>
    );
  }
  
  const formattedTime = lastUpdated ? 
    lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  
  return <div className="p-4 md:p-6">
      <div className="flex flex-col mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <h1 className="text-xl md:text-2xl font-semibold mb-3 md:mb-0">
            Welcome back, {adminUser.email?.split('@')[0] || 'Admin'}
          </h1>
          <div className={isMobile ? "mt-2" : ""}>
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
          </div>
        </div>
        
        {/* Only show live visitors when dateRange is 'today' */}
        {dateRange === 'today' && (
          <div className="flex items-center mt-2 text-sm text-gray-600">
            <Users className="h-4 w-4 mr-1 text-green-600" />
            <span className="mr-2">Live Visitors:</span>
            {liveVisitors !== null ? (
              <span className="font-medium">{liveVisitors}</span>
            ) : (
              <Skeleton className="h-4 w-10" />
            )}
            
            {isPolling && !liveVisitorsError && (
              <span className="ml-2 relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
            )}

            {liveVisitorsError && (
              <span className="ml-2 flex items-center text-amber-600 text-xs">
                <Info className="h-3 w-3 mr-1" />
                {liveVisitorsError}
              </span>
            )}
            
            {lastUpdated && !liveVisitorsError && (
              <span className="ml-2 text-xs text-gray-400">
                Updated: {formattedTime}
              </span>
            )}
          </div>
        )}
      </div>
      
      <div className="mobile-grid mb-6 md:mb-8">
        <Card className="bg-card rounded-lg shadow-md card-shadow-dark p-3 md:p-4">
          <h2 className="text-base md:text-lg font-semibold mb-1 md:mb-2">Total Users</h2>
          <p className="text-2xl md:text-3xl">{stats?.totalUsers || 0}</p>
        </Card>
        
        <Card className="bg-card rounded-lg shadow-md card-shadow-dark p-3 md:p-4">
          <h2 className="text-base md:text-lg font-semibold mb-1 md:mb-2">Paid Courses</h2>
          <p className="text-2xl md:text-3xl">{stats?.totalCourses || 0}</p>
        </Card>
        
        <Card className="bg-card rounded-lg shadow-md card-shadow-dark p-3 md:p-4">
          <h2 className="text-base md:text-lg font-semibold mb-1 md:mb-2">Total Sales</h2>
          <p className="text-2xl md:text-3xl">${stats?.totalRevenue || 0}</p>
        </Card>
      </div>

      <div className={isMobile ? "scale-[0.98] origin-left -ml-1" : ""}>
        <DashboardCharts 
          visitors={stats?.totalUsers || 0} 
          signups={stats?.totalCourses || 0} 
          revenue={stats?.totalRevenue || 0} 
          dateRange={dateRange}
          timeSeriesData={stats?.timeSeriesData}
          isLoading={false}
        />
      </div>
    </div>;
}
